import { Injectable } from '@angular/core';
import { AppHttpService, AuthService } from 'src/app/shared/services';
import { ApprovalQueueMeta, ApprovalQueueResponse, DateRange } from './approval-queue.models';

@Injectable({ providedIn: 'root' })
export class ApprovalQueueService {
  /** Meta from the most recent fetch, so the header and page share one call. */
  lastMeta: ApprovalQueueMeta | null = null;

  /**
   * In-flight or settled unfiltered fetch, shared by the /projects strip and the
   * page so boot costs one request. Scoped to the account it was fetched for:
   * a root service outlives login, and logout only clears NgRx state, so an
   * unscoped cache served the previous user's queue to the next one.
   */
  private unfiltered: Promise<ApprovalQueueResponse> | null = null;
  private unfilteredFor: string | null = null;

  constructor(private appHttpService: AppHttpService, private authService: AuthService) { }

  /**
   * Fetches the queue. An unfiltered fetch is cached per logged-in user; any
   * range always goes to the server. `opts.fresh` forces a new unfiltered
   * request even when a cached one already exists — the approvals page uses
   * it so counts don't go stale across a drill-in-and-return trip.
   */
  fetch(range: DateRange = { startDate: null, endDate: null }, opts: { fresh?: boolean } = {}): Promise<ApprovalQueueResponse> {
    if (!range.startDate && !range.endDate) {
      const user = this.currentUserId();
      if (opts.fresh || !this.unfiltered || this.unfilteredFor !== user) {
        this.lastMeta = null;
        this.unfilteredFor = user;
        this.unfiltered = this.request(range).catch(err => {
          // Don't cache a failure — the next caller should get a fresh try.
          this.unfiltered = null;
          this.unfilteredFor = null;
          throw err;
        });
      }
      return this.unfiltered;
    }
    return this.request(range);
  }

  /**
   * The unfiltered queue's meta, for the /projects entry strip. null when the
   * probe fails — a broken request hides the strip rather than breaking the
   * page. Uses the cached unfiltered fetch, so opening /approvals afterwards
   * costs no second request.
   */
  queueSummary(): Promise<ApprovalQueueMeta | null> {
    return this.fetch()
      .then(resp => resp.meta || null)
      .catch(() => null);
  }

  /** The cache key. null when nobody is logged in, so a logout also invalidates. */
  private currentUserId(): string | null {
    const id = this.authService.getCurrentUser()?.id;
    return id ? String(id) : null;
  }

  private request(range: DateRange): Promise<ApprovalQueueResponse> {
    const qp: Record<string, string> = {};
    if (range.startDate) qp.start_date = range.startDate;
    if (range.endDate) qp.end_date = range.endDate;

    return this.appHttpService.getService('/v1/approval-queue', qp).toPromise()
      .then((resp: any) => {
        if (!resp || !Array.isArray(resp.data)) {
          throw new Error('Error parsing response');
        }
        const parsed: ApprovalQueueResponse = { data: resp.data, meta: resp.meta };
        this.lastMeta = parsed.meta;
        return parsed;
      });
  }
}
